import type { ProgressReport } from '@light2live/shared-types';
import { seriesFor, type MetricGroup } from '@/lib/progress-trend';
import { MetricRow } from './MetricRow';

/**
 * One group of metrics as a card (#241 part B).
 *
 * Body / Energy and sleep / Mood, instead of the flat divided list P3 shipped.
 * The list was not wrong so much as unreadable at length: weight in kilograms,
 * energy out of ten and a count of feelings, stacked with nothing to say which
 * question each answers.
 *
 * The heading is a real <h3> under the page's <h1>, not a styled div, so the
 * groups are navigable by heading rather than only visible as boxes.
 */
export function MetricGroupCard({
    group,
    reports,
    children,
}: {
    group: MetricGroup;
    reports: ProgressReport[];
    children?: React.ReactNode;
}) {
    return (
        <section
            aria-labelledby={`group-${group.key}`}
            className="rounded-2xl border border-brand-surface-edge bg-brand-surface p-5 shadow-sm sm:p-6"
        >
            <h3 id={`group-${group.key}`} className="text-xl font-bold text-gray-900">
                {group.label}
            </h3>
            <p className="mt-1 text-base text-gray-700">{group.blurb}</p>

            <div className="mt-4 divide-y divide-gray-200">
                {group.metrics.map((metric) => (
                    <MetricRow
                        key={metric.key}
                        metric={metric}
                        points={seriesFor(reports, metric)}
                    />
                ))}
            </div>

            {children}
        </section>
    );
}
